<template>
  <el-card>
    <el-form label-position="top" :inline="true" size="small">
      <el-collapse v-model="activePanels">
        <el-collapse-item name="filters">
          <template #title>
            <h3>Filters</h3>
          </template>
          <el-row>
            <el-col :span="4" :xs="24">
              <el-form-item label="Search">
                <el-input
                  v-model="filter.query"
                  placeholder="Search"
                  @keyup.enter="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="12">
              <el-form-item label="Ad type">
                <AdTypeSelect
                  v-model="filter.type_id"
                  :with-empty="false"
                  size="small"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="12">
              <el-form-item label="Stream">
                <StreamSelect
                  v-model="filter.stream"
                  size="small"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="24">
              <el-form-item label="Country">
                <CountrySelect
                  v-model="filter.countries_ids"
                  :items="countries"
                  :multiple="true"
                  :use-id="true"
                  size="small"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8" :xs="24">
              <DeviceSelector
                v-model="filter.device_ids"
                @update:modelValue="update"
              />
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="4" :xs="24">
              <el-form-item label="Size">
                <SizeSelect
                  v-model="filter.size_id"
                  includeAdaptive
                  :disabled="filter.type_id !== AdType.Banner"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="24">
              <el-form-item label="Banner position">
                <el-select
                  v-model="filter.position"
                  multiple
                  :disabled="filter.type_id !== AdType.Banner"
                  @update:modelValue="update"
                >
                  <el-option
                    v-for="item in BannerPositionLabels"
                    :key="item.id"
                    :value="item.id"
                    :label="item.label"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="24">
              <el-form-item label="Banner type">
                <el-select
                  v-model="filter.banner_type"
                  multiple
                  :disabled="filter.type_id !== AdType.Banner"
                  @update:modelValue="update"
                >
                  <el-option
                    v-for="item in BannerTypeLabels"
                    :key="item.id"
                    :value="item.id"
                    :label="item.label"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="4" :xs="12">
              <el-form-item label="Traffic quality">
                <TrafficSelect
                  v-model="filter.traffic_qualities"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="12" :md="4" :sm="5">
              <el-form-item label="Pub type">
                <PubTypeSelect
                  v-model="filter.pub_types"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="12" :md="4" :sm="5">
              <el-form-item label="Availability">
                <AvailabilitySelect
                  v-model="filter.availability"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row style="margin-top: 18px">
            <el-col :span="5" :xs="14" :sm="8">
              <el-button
                type="primary"
                :icon="isDesktop ? 'RefreshElementIcon' : ''"
                round
                @click="update"
              >
                Refresh
              </el-button>
              <el-button
                type="success"
                :icon="isDesktop ? 'CloseElementIcon' : ''"
                round
                @click="reset"
              >
                Clear filters
              </el-button>
            </el-col>
          </el-row>
        </el-collapse-item>
      </el-collapse>
    </el-form>
  </el-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useCountriesStore } from '@/store/modules/countries'
import { AdType } from '@/api/types'
import { BannerPositionLabels, BannerTypeLabels } from '@/apps/traforama/lables'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import StreamSelect from '@/components/Select/StreamSelect.vue'
import TrafficSelect from '@/components/Select/TrafficSelect.vue'
import SizeSelect from '@/components/Select/SizeSelect.vue'
import AvailabilitySelect from '@/apps/traforama/components/AvailabilitySelect.vue'
import PubTypeSelect from '@/apps/traforama/components/PubTypeSelect.vue'
import DeviceSelector from '@/apps/traforama/components/DeviceSelector.vue'
import CountrySelect from '@/components/Select/CountrySelect.vue'
import useResize from '@/hooks/useResize'
import useQueryFilters from '@/hooks/useQueryFilters'

export default defineComponent({
  name: 'FilterPanel',
  components: {
    AdTypeSelect,
    StreamSelect,
    TrafficSelect,
    AvailabilitySelect,
    PubTypeSelect,
    DeviceSelector,
    CountrySelect,
    SizeSelect
  },
  props: {
    withSelected: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Object,
      required: true
    }
  },
  setup(props, context) {
    const countriesStore = useCountriesStore()

    const { isDesktop } = useResize()
    const { getQueryFilters, setQueryFilters } = useQueryFilters({
      type_id: 'int',
      stream: 'int',
      size_id: 'int',
      query: 'str',
      traffic_qualities: 'array-int',
      availability: 'array-bool',
      pub_types: 'array-bool',
      device_ids: 'array-int',
      countries_ids: 'array-str',
      banner_type: 'array-str',
      position: 'array-str'
    })

    const activePanels = ref(isDesktop.value ? ['filters', 'pressets'] : [])
    const defaultFilters = { ...props.modelValue }
    const filter = ref({ ...defaultFilters, ...getQueryFilters() })

    const update = (newValue?: any) => {
      if (filter.value.type_id !== AdType.Banner) {
        filter.value.banner_type = null
        filter.value.size_id = null
        filter.value.position = null
      }
      setQueryFilters(filter.value)
      context.emit('update:modelValue', filter.value)
      context.emit('refresh')
    }

    const reset = async () => {
      filter.value = { ...defaultFilters }
      setQueryFilters(defaultFilters)
      update()
    }

    const countries = computed(() => countriesStore.sorted)

    onMounted(() => {
      if (Object.keys(getQueryFilters()).length) {
        context.emit('update:modelValue', filter.value)
      } else {
        setQueryFilters(filter.value)
      }
      context.emit('refresh')
    })

    return {
      update,
      filter,
      countries,
      AdType,
      reset,
      isDesktop,
      activePanels,
      BannerPositionLabels,
      BannerTypeLabels
    }
  }
})
</script>

<style lang="scss" scoped>
.el-select {
  width: 100%;
}

:deep(.el-card__body) {
  padding: 0px 5px;
}

.el-form-item {
  width: 90%;
}

h3 {
  margin: 2px 8px 5px 8px;
  padding-top: 5px;
}

:deep(.selected-checkbox .el-form-item__label) {
  padding-right: 5px;
}

:deep(.el-collapse) {
  width: 100%;
}

:deep(.el-collapse-item__content) {
  padding-bottom: 5px;
}

:deep(.el-form-item--small .el-form-item__label) {
  padding: 0;
}

:deep(.el-form-item--small.el-form-item) {
  margin-bottom: 0;
}

.el-row {
  width: 100%;
}
</style>
